import type {
  GameState,
  PendingEffect,
  ResponseWindow,
  ResponseEntry,
} from "@/types/game";
import type { ResponseKind, DeckCard } from "@/types/cards";
import { computeDamage } from "./damage";
import { isFrozen, isEffectLocked } from "./Status";

// 创建待结算效果
export function makePendingEffect(
  partial: Partial<PendingEffect> & Pick<PendingEffect, "source" | "target">
): PendingEffect {
  return {
    nonTargetable: false,
    allowResponses: [],
    status: "await-response",
    ...partial,
  };
}

// 第一目标 id
export function primaryTarget(effect: PendingEffect): string {
  return Array.isArray(effect.target) ? effect.target[0] : effect.target;
}

// 开启响应窗口
//   攻击(w/hw)/法术(f/sf)：目标+目标队友可响应
//   多目标效果（乱符千吻）：所有目标可响应
//   摄魂(ss)/mana：仅目标
//   补给(hp/mp heal)：对方队伍可响应（拒援——被治疗方不会拒援自己）
export function openResponseWindow(
  state: GameState,
  effect: PendingEffect
): GameState {
  if (effect.nonTargetable) {
    return { ...state, phase: "response", responseWindow: null };
  }
  const responders = new Set<string>();
  if (Array.isArray(effect.target)) {
    // 多目标：所有目标均可响应
    for (const tid of effect.target) responders.add(tid);
  } else {
    const tid = effect.target;
    // 补给效果（拒援）：对方队伍可响应（被治疗方不会拒援自己）
    if (effect.allowResponses.includes("拒援")) {
      const source = state.players.find((p) => p.id === effect.source);
      if (source) {
        state.players
          .filter((p) => p.team !== source.team && p.isAlive)
          .forEach((m) => responders.add(m.id));
      }
    } else {
      responders.add(tid);
      const isSoul =
        effect.damageType === "ss" ||
        effect.damageType === "mana" ||
        effect.damageType === undefined;
      if (!isSoul) {
        const target = state.players.find((p) => p.id === tid);
        if (target) {
          state.players
            .filter((p) => p.team === target.team && p.id !== tid && p.isAlive)
            .forEach((m) => responders.add(m.id));
        }
      }
    }
  }
  const decisions: ResponseWindow["decisions"] = {};
  for (const rid of responders) decisions[rid] = "pending";
  const window: ResponseWindow = {
    effect,
    responders: Array.from(responders),
    decisions,
  };
  return { ...state, phase: "response", responseWindow: window };
}

// 某玩家能否以 kind 响应当前窗口
//   闪避/虚灵/拒援：任何响应者均可出
//   反射/无敌/解除：仅目标
//   限制：寒冰冻结 → 不能打出任何手牌；无间炼狱 → 不能打出效果牌（响应牌都是效果牌）
//   恶魔：目标本人不能闪避，需队友闪避（P2 简化：队友 1 张闪避即可生效）
export function canRespondWith(
  state: GameState,
  playerId: string,
  kind: ResponseKind
): boolean {
  const w = state.responseWindow;
  if (!w || !w.effect.allowResponses.includes(kind)) return false;
  if (!w.responders.includes(playerId)) return false;
  const player = state.players.find((p) => p.id === playerId);
  if (!player) return false;
  if (isFrozen(player)) return false; // 寒冰：回合外也不能打手牌
  if (isEffectLocked(player)) return false; // 无间炼狱：响应牌属于效果牌
  const target = primaryTarget(w.effect);
  if (w.effect.attackExtras?.includes("恶魔") && kind === "闪避" && playerId === target) {
    return false; // 恶魔：目标本人不能闪避
  }
  if (kind === "闪避" || kind === "虚灵" || kind === "拒援") return true;
  return playerId === target;
}

// 玩家做出响应决策：pass / 出牌响应
export function setResponseDecision(
  state: GameState,
  playerId: string,
  decision: "pass" | { card: DeckCard; kind: ResponseKind; virtual?: boolean }
): GameState {
  if (!state.responseWindow) {
    throw new Error("无活动响应窗口");
  }
  const value: ResponseEntry | "pass" =
    decision === "pass"
      ? "pass"
      : { playerId, card: decision.card, kind: decision.kind, virtual: decision.virtual };
  const decisions: ResponseWindow["decisions"] = {
    ...state.responseWindow.decisions,
    [playerId]: value,
  };
  return {
    ...state,
    responseWindow: {
      ...state.responseWindow,
      decisions,
    },
  };
}

// 是否所有人都已决策
export function allResponded(state: GameState): boolean {
  if (!state.responseWindow) return true;
  return Object.values(state.responseWindow.decisions).every(
    (d) => d !== "pending"
  );
}

// 响应优先级：无敌 > 解除 > 闪避/虚灵 > 拒援 > 反射
const PRIORITY: Record<ResponseKind, number> = {
  无敌: 5,
  解除: 4,
  闪避: 3,
  虚灵: 3,
  拒援: 2,
  反射: 1,
};

// 收集所有已出的响应（按优先级降序）
export function collectResponses(state: GameState): ResponseEntry[] {
  if (!state.responseWindow) return [];
  const out: ResponseEntry[] = [];
  for (const v of Object.values(state.responseWindow.decisions)) {
    if (v !== "pending" && v !== "pass") out.push(v);
  }
  out.sort((a, b) => PRIORITY[b.kind] - PRIORITY[a.kind]);
  return out;
}

// 结算响应窗口：应用最高优先级响应，弃掉所有已用响应牌（离开手牌）
// 返回 { state, outcome }
//   cancelled: 无敌/闪避/虚灵/拒援 生效，原效果取消
//   reflect:   反射生效，需要产生反向效果
//   none:      全部 pass，原效果照常结算
export function resolveWindow(state: GameState): {
  state: GameState;
  outcome: "cancelled" | "reflect" | "none";
  winningKind: ResponseKind | null;
} {
  const w = state.responseWindow;
  if (!w) return { state, outcome: "none", winningKind: null };
  const responses = collectResponses(state);

  // 所有已用响应牌：离开持有者手牌，进入弃牌堆
  // 虚拟牌（替换打出）：uid=-1，2张真牌已在响应时消耗，跳过
  const usedUids = new Set(
    responses.filter((r) => !r.virtual).map((r) => r.card.uid)
  );
  let discardPile = [...state.discardPile];
  for (const r of responses) {
    if (r.virtual) continue; // 虚拟牌不入弃牌堆
    discardPile.push(r.card);
  }
  const players = state.players.map((p) =>
    p.hand.some((c) => usedUids.has(c.uid))
      ? { ...p, hand: p.hand.filter((c) => !usedUids.has(c.uid)) }
      : p
  );

  // 响应事件
  const log = [...state.log];
  for (const r of responses) {
    log.push({
      type: "response",
      playerId: r.playerId,
      kind: r.kind,
      cardId: r.card.id,
    });
  }

  const next: GameState = {
    ...state,
    players,
    discardPile,
    log,
    phase: "play",
    responseWindow: null,
  };

  if (responses.length === 0) {
    return { state: next, outcome: "none", winningKind: null };
  }
  const winner = responses[0];
  if (winner.kind === "反射") {
    return { state: next, outcome: "reflect", winningKind: "反射" };
  }
  return {
    state: next,
    outcome: "cancelled",
    winningKind: winner.kind,
  };
}

// 构造反射反向效果：
//   反弹伤害 = 原效果本应造成的最终伤害（对原目标结算一次），直结到原来源
//   反射链上不允许再次反射，防止无限循环
export function makeReflectedEffect(
  state: GameState,
  effect: PendingEffect
): PendingEffect {
  const tid = primaryTarget(effect);
  const target = state.players.find((p) => p.id === tid);
  const source = state.players.find((p) => p.id === effect.source)!;
  let finalDamage = effect.damageAmount ?? 0;
  if (target && effect.damageType) {
    finalDamage = computeDamage(
      effect.damageType,
      effect.damageAmount ?? 0,
      source,
      target,
      effect.multiplier ?? 1
    ).finalDamage;
  }
  return {
    ...effect,
    source: tid,
    target: effect.source,
    damageAmount: finalDamage,
    multiplier: 1,
    direct: true,
    allowResponses: effect.allowResponses.filter((k) => k !== "反射"),
    attackExtras: undefined, // 反弹不重触发 TG 附加效果
    skillStatuses: undefined,
    status: "await-response",
  };
}
